import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IdentityServiceBase } from '@seahorse/auth';
import {
  Languages,
  PreloaderServiceBase,
  ResultStatus,
} from '@seahorse/common';
import {
  PreferenceDataService,
  PreferenceModel,
  UsersDataService,
} from '@seahorse/domain';
import { DialogService } from '@seahorse/shell';
import { forkJoin, interval, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrganisationStoreService } from '../../shared/services/organisation-store.service';
import { ProxyServices } from '../../shared/services/proxy.service';
import { ProfileDataService } from '../../modules/profiles/services/profile-data.service';

@Injectable({
  providedIn: 'root',
})
export class PreloaderService extends PreloaderServiceBase {
  private _identityService = inject(IdentityServiceBase);
  private _dialog = inject(DialogService);
  private _userDataService = inject(UsersDataService);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _preferenceDataService = inject(PreferenceDataService);
  private _profileDataService = inject(ProfileDataService);
  private _proxyServices = inject(ProxyServices);
  private _orgStore = inject(OrganisationStoreService);
  private _destroy = new Subject();

  loadIdentity(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      // Try to load the identity from the API
      if (this._identityService.token) {
        // Preload all users
        const preferences = this.getPreferences();

        // Preload products
        const products = this._profileDataService.getProducts();

        forkJoin([preferences, products, this._orgStore.load()]).subscribe(
          (results) => {
            const preferences = results[0];
            const products = results[1];

            if (this._identityService.identity) {
              const customDataPermissionsPreference = preferences.find(
                (x) =>
                  x.category?.toLowerCase() === 'permissions' &&
                  x.name.toLowerCase() === 'custom_data'
              )?.fieldValue;

              if (customDataPermissionsPreference) {
                const customDataPermissions = JSON.parse(
                  customDataPermissionsPreference
                );
                if (customDataPermissions.hasStep) {
                  const invoicesPermissionsPreference = preferences.find(
                    (x) =>
                      x.category?.toLowerCase() === 'permissions' &&
                      x.name.toLowerCase() === 'invoices'
                  )?.fieldValue;
                  const invoicesPermissions = invoicesPermissionsPreference
                    ? JSON.parse(invoicesPermissionsPreference)
                    : undefined;
                  if (
                    customDataPermissions.finished &&
                    invoicesPermissions?.hasStep &&
                    !invoicesPermissions?.finished
                  ) {
                    this._router.navigate(['/onboarding/invoicing']);
                  } else {
                    this._router.navigate(['/onboarding/customdata']);
                  }
                }
              }
            } else {
              this._identityService.clearIdentity();
            }

            if (products.status === ResultStatus.OK) {
              this._proxyServices.products = products.result;
            }

            interval(30000)
              .pipe(takeUntil(this._destroy))
              .subscribe(() => {
                this._userDataService.getProfile().subscribe({
                  next: (userResult) => {
                    if (!userResult.hasResult) {
                      this.onUserUnauthorized();
                    }
                  },
                  error: (error: HttpErrorResponse) => {
                    if (error.status === 401) {
                      this.onUserUnauthorized();
                    }
                  },
                });
              });

            subscriber.next(true);
          },
          () => {
            this._identityService.clearIdentity();
          }
        );
      } else {
        subscriber.next(true);
      }
    });
  }

  private onUserUnauthorized() {
    // No OK button and no way to close it: the app logs out by itself.
    this._dialog
      .alert({
        title: 'You are not logged in anymore',
        message: 'Logging out...',
        showOk: false,
        closable: false,
      })
      .subscribe();

    interval(5000)
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        this._identityService.clearIdentity();
      });
  }

  preload(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.loadIdentity().subscribe(() => {
        subscriber.next(true);
      });
    });
  }

  private getPreferences(): Observable<PreferenceModel[]> {
    return new Observable<PreferenceModel[]>((subscription) => {
      let result: PreferenceModel[] = [];
      this._preferenceDataService.get().subscribe({
        next: (response) => {
          if (response.hasResult) {
            result = response.result;
          }
        },
        error: () => {
          subscription.next(result);
          subscription.complete();
        },
        complete: () => {
          if (result) {
            this._identityService.setPreferences(result);
            const language = Languages.find(
              (x) =>
                x.value.toLowerCase() ===
                result
                  .find((y) => y.name.toLowerCase() === 'language')
                  ?.fieldValue?.toLowerCase()
            )?.value;
            if (language) {
              this._translateService.use(language).subscribe({
                complete: () => {
                  subscription.next(result);
                  subscription.complete();
                },
              });
            } else {
              subscription.next(result);
              subscription.complete();
            }
          }
        },
      });
    });
  }
}
